/**
 * Leviathan Web - Invoice Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle } from 'lucide-react';
import { API_URL } from '@/config/api';

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  isTaxable: boolean;
  /**
   * Where this line's fuel was lifted, snapshotted at invoicing.
   *
   * `originIsAssumed` is the one that matters on screen: tax liability follows
   * the movement, so a load lifted in Texas and delivered to Louisiana owes
   * Louisiana. When the origin could not be traced the engine assumes the
   * movement was intra-state — which may be wrong, and the user has to be able
   * to see that the figure rests on an assumption rather than a fact.
   */
  originTerminalName?: string | null;
  originState?: string | null;
  originSource?: string | null;
  originIsAssumed?: boolean;
  product?: {
    id: string;
    name: string;
    sku: string;
  };
}

interface Payment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  amount: number;
  paymentMethod: string;
  referenceNumber: string | null;
  notes: string | null;
  createdAt: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: string;
  paymentTerms: string;
  poNumber: string | null;
  billingName: string | null;
  billingAddress: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string | null;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discountAmount: number;
  shippingAmount: number;
  total: number;
  amountPaid: number;
  amountDue: number;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  updatedAt: string;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
    email: string | null;
    phone: string | null;
  };
  order?: {
    id: string;
    orderNumber: string;
  };
  items: InvoiceItem[];
  payments: Payment[];
}

const VALID_STATUS_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['PENDING', 'VOID'],
  PENDING: ['SENT', 'VOID'],
  SENT: ['VIEWED', 'PARTIAL', 'PAID', 'VOID'],
  VIEWED: ['PARTIAL', 'PAID', 'VOID'],
  PARTIAL: ['PAID', 'VOID'],
  PAID: [],
  OVERDUE: ['PARTIAL', 'PAID', 'WRITTEN_OFF', 'VOID'],
  VOID: [],
  WRITTEN_OFF: [],
};

const InvoiceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'CHECK',
    paymentDate: new Date().toISOString().split('T')[0] || '',
    referenceNumber: '',
    notes: '',
  });
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
        // Set default payment amount to amount due
        setPaymentData((prev) => ({
          ...prev,
          amount: data.amountDue.toString(),
        }));
      } else {
        setError('Invoice not found');
      }
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      setError('Failed to load invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus: string) => {
    if (!invoice) return;
    setIsUpdating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/invoices/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setError('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const recordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;
    setIsRecordingPayment(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/invoices/${id}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          amount: paymentData.amount,
          paymentMethod: paymentData.paymentMethod,
          paymentDate: paymentData.paymentDate || null,
          referenceNumber: paymentData.referenceNumber || null,
          notes: paymentData.notes || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(data);
        setShowPaymentModal(false);
        setPaymentData({
          amount: data.amountDue.toString(),
          paymentMethod: 'CHECK',
          paymentDate: new Date().toISOString().split('T')[0] || '',
          referenceNumber: '',
          notes: '',
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      setError('Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const deleteInvoice = async () => {
    if (!invoice || invoice.status !== 'DRAFT') return;
    if (!confirm('Are you sure you want to delete this invoice?')) return;

    try {
      const response = await fetch(`${API_URL}/api/invoices/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        navigate('/invoices');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete invoice');
      }
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      setError('Failed to delete invoice');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (['PAID', 'VOID', 'WRITTEN_OFF'].includes(status)) return false;
    return new Date(dueDate) < new Date();
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      case 'PENDING':
        return 'text-warning-amber bg-warning-amber/20 border-warning-amber/30';
      case 'SENT':
        return 'text-steel bg-steel/20 border-steel/30';
      case 'VIEWED':
        return 'text-steel bg-steel/20 border-steel/30';
      case 'PARTIAL':
        return 'text-warning-amber bg-warning-amber/20 border-warning-amber/30';
      case 'PAID':
        return 'text-success-green bg-success-green/20 border-success-green/30';
      case 'OVERDUE':
        return 'text-error-red bg-error-red/20 border-error-red/30';
      case 'VOID':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      case 'WRITTEN_OFF':
        return 'text-error-red bg-error-red/20 border-error-red/30';
      default:
        return 'text-text-secondary bg-text-secondary/20 border-text-secondary/30';
    }
  };

  const getStatusButtonColor = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-warning-amber/20 hover:bg-warning-amber/30 text-warning-amber border-warning-amber/30';
      case 'SENT':
        return 'bg-steel/20 hover:bg-steel/30 text-steel border-steel/30';
      case 'VIEWED':
        return 'bg-steel/20 hover:bg-steel/30 text-steel border-steel/30';
      case 'PARTIAL':
        return 'bg-warning-amber/20 hover:bg-warning-amber/30 text-warning-amber border-warning-amber/30';
      case 'PAID':
        return 'bg-success-green/20 hover:bg-success-green/30 text-success-green border-success-green/30';
      case 'VOID':
        return 'bg-text-tertiary/20 hover:bg-text-tertiary/30 text-text-tertiary border-text-tertiary/30';
      case 'WRITTEN_OFF':
        return 'bg-error-red/20 hover:bg-error-red/30 text-error-red border-error-red/30';
      default:
        return 'bg-panel-border hover:bg-steel/20 text-text-primary border-panel-border';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading invoice...</div>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="text-error-red font-mono mb-4">&gt; {error}</div>
        <Link to="/invoices" className="text-steel hover:text-amber font-mono transition-colors">
          &larr; Back to Invoices
        </Link>
      </div>
    );
  }

  if (!invoice) return null;

  const validTransitions = VALID_STATUS_TRANSITIONS[invoice.status] || [];
  const canRecordPayment = ['SENT', 'VIEWED', 'PARTIAL', 'OVERDUE'].includes(invoice.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4">
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  {invoice.invoiceNumber}
                </h1>
                <span
                  className={`px-3 py-1 font-mono text-sm uppercase tracking-wider border ${getStatusColor(invoice.status)}`}
                >
                  {invoice.status}
                </span>
                {isOverdue(invoice.dueDate, invoice.status) && invoice.status !== 'OVERDUE' && (
                  <span className="px-3 py-1 font-mono text-sm uppercase tracking-wider border text-error-red bg-error-red/20 border-error-red/30 animate-pulse">
                    OVERDUE
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/invoices"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Invoices
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {canRecordPayment && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="px-6 py-2 bg-success-green/20 hover:bg-success-green/30 text-success-green font-mono text-sm uppercase tracking-wider transition-all border border-success-green/30"
                >
                  Record Payment
                </button>
              )}
              {invoice.status === 'DRAFT' && (
                <button
                  onClick={deleteInvoice}
                  className="px-4 py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono text-sm uppercase tracking-wider transition-all border border-error-red/30"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Status Actions */}
      {validTransitions.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Status Actions
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {validTransitions.map((status) => (
                <button
                  key={status}
                  onClick={() => updateStatus(status)}
                  disabled={isUpdating}
                  className={`px-6 py-2 font-mono text-sm uppercase tracking-wider transition-all border disabled:opacity-50 ${getStatusButtonColor(status)}`}
                >
                  {isUpdating ? 'Updating...' : `Mark as ${status.replace('_', ' ')}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={formatCurrency(invoice.total)} color="amber" />
        <StatCard label="Amount Paid" value={formatCurrency(invoice.amountPaid)} color="green" />
        <StatCard
          label="Amount Due"
          value={formatCurrency(invoice.amountDue)}
          color={invoice.amountDue > 0 ? 'red' : 'green'}
        />
        <StatCard label="Due Date" value={formatDate(invoice.dueDate)} color="steel" />
      </div>

      {/* Invoice Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Bill To
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <Link
                  to={`/customers/${invoice.customer.id}`}
                  className="text-text-primary font-mono font-semibold text-lg hover:text-amber transition-colors"
                >
                  {invoice.billingName || invoice.customer.name}
                </Link>
                <div className="text-text-tertiary font-mono text-sm">
                  {invoice.customer.customerNumber}
                </div>
              </div>
              {invoice.billingAddress && (
                <div className="text-text-secondary font-mono text-sm">
                  <div>{invoice.billingAddress}</div>
                  <div>
                    {[invoice.billingCity, invoice.billingState].filter(Boolean).join(', ')}{' '}
                    {invoice.billingZip}
                  </div>
                </div>
              )}
              {invoice.customer.email && (
                <div className="text-text-secondary font-mono text-sm">
                  Email: {invoice.customer.email}
                </div>
              )}
              {invoice.customer.phone && (
                <div className="text-text-secondary font-mono text-sm">
                  Phone: {invoice.customer.phone}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Invoice Details
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Invoice Date</div>
                <div className="text-text-primary font-mono">{formatDate(invoice.invoiceDate)}</div>
              </div>
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Due Date</div>
                <div
                  className={`font-mono ${isOverdue(invoice.dueDate, invoice.status) ? 'text-error-red' : 'text-text-primary'}`}
                >
                  {formatDate(invoice.dueDate)}
                </div>
              </div>
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Payment Terms</div>
                <div className="text-text-primary font-mono">{invoice.paymentTerms}</div>
              </div>
              {invoice.poNumber && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">PO #</div>
                  <div className="text-text-primary font-mono">{invoice.poNumber}</div>
                </div>
              )}
              {invoice.order && (
                <div className="col-span-2">
                  <div className="text-text-tertiary font-mono uppercase text-xs">From Order</div>
                  <Link
                    to={`/orders/${invoice.order.id}`}
                    className="text-steel hover:text-amber font-mono transition-colors"
                  >
                    {invoice.order.orderNumber}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          <div className="p-6 border-b border-panel-border">
            <div className="flex items-center space-x-2">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Line Items
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background border-b border-panel-border">
                <tr>
                  <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Description
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Quantity
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Unit Price
                  </th>
                  <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Taxable
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Line Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item) => (
                  <tr key={item.id} className="border-b border-panel-border">
                    <td className="px-6 py-4">
                      <div className="text-text-primary font-mono font-semibold">
                        {item.description}
                      </div>
                      {item.product && (
                        <div className="text-text-tertiary font-mono text-xs">{item.product.sku}</div>
                      )}
                      {/* ORIGIN. Shown per line, because one invoice can bill
                          two loads from two racks in two states — and because a
                          tax figure resting on an ASSUMPTION about where the
                          fuel came from must not look identical to one resting
                          on a traced lifting. */}
                      {item.originIsAssumed ? (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono text-amber-400"
                          title={
                            'Tax was calculated assuming this load did not cross a state line, because ' +
                            'the lifting terminal is not recorded. If it did cross one, the excise is ' +
                            'attributed to the wrong state. Record the origin on the shipment.'
                          }
                        >
                          <AlertTriangle className="w-3 h-3" />
                          Origin assumed
                        </div>
                      ) : (
                        item.originTerminalName && (
                          <div className="mt-1 text-[11px] font-mono text-text-tertiary">
                            Lifted at {item.originTerminalName}
                            {item.originState ? ` (${item.originState})` : ''}
                          </div>
                        )
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-text-primary font-mono">
                      {Number(item.quantity).toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary font-mono">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="px-6 py-4 text-center text-text-secondary font-mono">
                      {item.isTaxable ? (
                        <span className="text-success-green">Yes</span>
                      ) : (
                        <span className="text-text-tertiary">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className="text-amber font-mono font-semibold"
                        style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                      >
                        {formatCurrency(item.lineTotal)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
            <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
              Totals
            </h3>
          </div>
          <div className="max-w-xs ml-auto space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary font-mono">Subtotal:</span>
              <span className="text-text-primary font-mono">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Tax ({invoice.taxRate}%):</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(invoice.taxAmount)}
                </span>
              </div>
            )}
            {invoice.discountAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Discount:</span>
                <span className="text-error-red font-mono">
                  -{formatCurrency(invoice.discountAmount)}
                </span>
              </div>
            )}
            {invoice.shippingAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Shipping:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(invoice.shippingAmount)}
                </span>
              </div>
            )}
            <div className="border-t border-panel-border pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-text-secondary font-mono font-semibold">Total:</span>
                <span
                  className="text-xl text-amber font-mono font-bold"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
                >
                  {formatCurrency(invoice.total)}
                </span>
              </div>
            </div>
            {invoice.amountPaid > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Amount Paid:</span>
                <span className="text-success-green font-mono">
                  -{formatCurrency(invoice.amountPaid)}
                </span>
              </div>
            )}
            <div className="border-t border-panel-border pt-2">
              <div className="flex justify-between">
                <span className="text-text-secondary font-mono font-semibold">Balance Due:</span>
                <span
                  className={`text-2xl font-mono font-bold ${invoice.amountDue > 0 ? 'text-error-red' : 'text-success-green'}`}
                  style={{
                    textShadow:
                      invoice.amountDue > 0
                        ? '0 0 10px rgba(220, 38, 38, 0.6)'
                        : '0 0 10px rgba(34, 197, 94, 0.6)',
                  }}
                >
                  {formatCurrency(invoice.amountDue)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {invoice.payments.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
            <div className="p-6 border-b border-panel-border">
              <div className="flex items-center space-x-2">
                <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                  Payment History
                </h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Payment #
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Date
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Method
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Reference
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-panel-border">
                      <td className="px-6 py-4">
                        <span className="text-text-primary font-mono font-semibold">
                          {payment.paymentNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono">
                        {formatDate(payment.paymentDate)}
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono">
                        {payment.paymentMethod.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono">
                        {payment.referenceNumber || '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-success-green font-mono font-semibold">
                          {formatCurrency(payment.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {(invoice.notes || invoice.internalNotes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {invoice.notes && (
            <div className="relative stat-card">
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                    Customer Notes
                  </h3>
                </div>
                <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">
                  {invoice.notes}
                </p>
              </div>
            </div>
          )}

          {invoice.internalNotes && (
            <div className="relative stat-card">
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                    Internal Notes
                  </h3>
                </div>
                <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">
                  {invoice.internalNotes}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="relative w-full max-w-lg mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                  <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
                    Record Payment
                  </h3>
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-text-tertiary hover:text-text-primary transition-colors"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={recordPayment} className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Amount <span className="text-amber">*</span>
                  </label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    step="0.01"
                    min="0.01"
                    max={invoice.amountDue}
                    required
                    className="form-input"
                    placeholder="0.00"
                  />
                  <div className="mt-1 text-text-tertiary font-mono text-xs">
                    Balance due: {formatCurrency(invoice.amountDue)}
                  </div>
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Payment Method <span className="text-amber">*</span>
                  </label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, paymentMethod: e.target.value })
                    }
                    required
                    className="form-input"
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="ACH">ACH / Bank Transfer</option>
                    <option value="WIRE">Wire Transfer</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                    <option value="DEBIT_CARD">Debit Card</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Payment Date <span className="text-amber">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, paymentDate: e.target.value })
                    }
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Reference # (Check #, Transaction ID, etc.)
                  </label>
                  <input
                    type="text"
                    value={paymentData.referenceNumber}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, referenceNumber: e.target.value })
                    }
                    className="form-input"
                    placeholder="Check # or Transaction ID"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Notes
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    className="form-input resize-none"
                    rows={2}
                    placeholder="Payment notes..."
                  />
                </div>

                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="px-6 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isRecordingPayment}
                    className="px-8 py-2 bg-success-green/20 hover:bg-success-green/30 text-success-green font-mono uppercase text-sm tracking-wider transition-all border border-success-green/30 disabled:opacity-50"
                  >
                    {isRecordingPayment ? 'Recording...' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Stat card component for summary stats
 */
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'amber' | 'green' | 'red' | 'steel';
}) {
  const colorClasses = {
    amber: 'text-amber',
    green: 'text-success-green',
    red: 'text-error-red',
    steel: 'text-steel',
  };

  const glowColors = {
    amber: 'rgba(255, 149, 0, 0.6)',
    green: 'rgba(34, 197, 94, 0.6)',
    red: 'rgba(220, 38, 38, 0.6)',
    steel: 'rgba(92, 140, 199, 0.6)',
  };

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 text-center">
        <div className="text-text-tertiary font-mono text-xs uppercase tracking-wider mb-1">
          {label}
        </div>
        <div
          className={`text-xl font-mono font-bold ${colorClasses[color]}`}
          style={{ textShadow: `0 0 8px ${glowColors[color]}` }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default InvoiceDetailPage;
